import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ModeToggle from "./ModeToggle";
import UserMenu from "./UserMenu";
import { Menu, X, ChevronDown } from "lucide-react";

// ── Grouped header navigation ────────────────────────────────────────────────
// Three top-level slots keep the bar uncluttered: Tools (product surfaces),
// Resources (editorial), Pricing (conversion). Home is reachable via the logo.
const TOOLS = [
  { to: "/ai",         label: "AI Query",   sub: "Natural-language intelligence" },
  { to: "/postcode",   label: "Postcode",   sub: "Free coverage + adoption check" },
  { to: "/compare",    label: "Compare",    sub: "Up to 5 LAs side-by-side" },
  { to: "/highlights", label: "Highlights", sub: "Live leaderboards by metric" },
  { to: "/reports",    label: "Reports",    sub: "Green Book-aligned exports" },
];
const RESOURCES = [
  { to: "/why-us", label: "Why us",  sub: "How we compare to alternatives" },
  { to: "/blog",   label: "Blog",    sub: "Long-form analysis + commentary" },
  { to: "/faq",    label: "FAQ",     sub: "Pricing, methodology, security" },
];

function HeaderDropdown({ label, items, testid }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const anyActive = items.some((i) => location.pathname.startsWith(i.to));
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
          anyActive ? "text-brand font-medium" : "text-[#5C5C59] hover:text-brand"
        }`}
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-[260px] rounded-xl border border-brand-border bg-white shadow-2xl overflow-hidden"
          data-testid={`${testid}-menu`}
        >
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-4 py-3 text-sm transition-colors ${
                  isActive ? "bg-brand-bg text-brand" : "text-[#111110] hover:bg-brand-bg hover:text-brand"
                }`
              }
            >
              <div className="font-medium">{it.label}</div>
              <div className="text-[11px] text-[#5C5C59] mt-0.5">{it.sub}</div>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicShell() {
  const { user } = useAuth();  // referenced for layout symmetry / future hooks
  void user;
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-brand-bg text-[#111110] font-body">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-6">
          {/* Brand */}
          <Link to="/" className="flex items-center" data-testid="brand-home" onClick={() => setMobileOpen(false)}>
            <img src="/brand/logo-dark-full.png" alt="Infralytics-GIIS" className="h-12 sm:h-14 w-auto object-contain" />
          </Link>

          {/* Desktop nav — 3 slots only */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="public-nav">
            <HeaderDropdown label="Tools" items={TOOLS} testid="nav-tools" />
            <HeaderDropdown label="Resources" items={RESOURCES} testid="nav-resources" />
            <NavLink
              to="/pricing"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded-md transition-colors ${
                  isActive ? "text-brand font-medium" : "text-[#5C5C59] hover:text-brand"
                }`
              }
              data-testid="nav-pricing"
            >
              Pricing
            </NavLink>
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block"><ModeToggle /></div>
            <UserMenu mode="standard" />
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 -mr-1 text-[#5C5C59] hover:text-brand"
              aria-label="Toggle menu"
              data-testid="mobile-nav-toggle"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer — grouped sections, not a 10-item dump */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-brand-border bg-white/95 backdrop-blur-xl" data-testid="mobile-nav-panel">
            <nav className="max-w-[1400px] mx-auto px-6 py-4 space-y-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C59] mb-2">Tools</div>
                <div className="grid grid-cols-2 gap-1">
                  {TOOLS.map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive ? "text-brand font-medium bg-brand-bg" : "text-[#5C5C59] hover:text-brand hover:bg-brand-bg"
                        }`
                      }>
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C59] mb-2">Resources</div>
                <div className="grid grid-cols-2 gap-1">
                  {RESOURCES.map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive ? "text-brand font-medium bg-brand-bg" : "text-[#5C5C59] hover:text-brand hover:bg-brand-bg"
                        }`
                      }>
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <NavLink to="/pricing" onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded-md font-medium transition-colors ${
                    isActive ? "text-white bg-[#E55B13]" : "text-white bg-[#061426] hover:bg-[#0A1F33]"
                  }`
                }>
                Pricing →
              </NavLink>
              <div className="pt-2 border-t border-brand-border sm:hidden">
                <ModeToggle />
              </div>
            </nav>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="bg-brand text-white mt-24">
        <div className="max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img src="/brand/logo-light-full.png" alt="Infralytics-GIIS" className="h-16 w-auto object-contain mb-5" />
            <p className="text-white/70 text-sm max-w-md leading-relaxed">
              Policy-grade digital infrastructure intelligence — currently live across the <span className="text-white font-medium">North East of England</span> as a regional pilot. Replacing consultancy and analyst overhead with verified, real-time insight for councils, combined authorities, DSIT and the wider broadband ecosystem. National rollout follows.
            </p>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Platform</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ai" className="hover:text-brand-accent transition-colors">AI Query</Link></li>
              <li><Link to="/postcode" className="hover:text-brand-accent transition-colors">Gigabit Checker</Link></li>
              <li><Link to="/compare" className="hover:text-brand-accent transition-colors">Compare</Link></li>
              <li><Link to="/reports" className="hover:text-brand-accent transition-colors">Reports</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Resources</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/blog" className="hover:text-brand-accent transition-colors">Blog</Link></li>
              <li><Link to="/faq" className="hover:text-brand-accent transition-colors">FAQ</Link></li>
              <li><Link to="/highlights" className="hover:text-brand-accent transition-colors">Highlight Reel</Link></li>
              <li><a href="mailto:hello@nexintell.co" className="hover:text-brand-accent transition-colors">Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/50">
            <span>© {new Date().getFullYear()} Nexintell · Infralytics-GIIS</span>
            <div className="flex items-center gap-5">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">
                Terms of Use
              </Link>
              <span className="font-mono uppercase tracking-wider">Policy-Grade Intelligence</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
